import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Suspense } from "react";
import { AuthProvider } from "./auth/contexts/AuthContext";
import ProtectedRoute from "./auth/components/ProtectedRoute";
import AdminRoute from "./auth/components/AdminRoute";
import { useActivityLogger } from "./hooks/useActivityLogger";
import QueryProvider from "./lib/providers";
import { Toaster } from "./components/ui/sonner";
import {
  LoadingErrorBoundary,
  LoadingSpinner,
} from "./components/LoadingBoundary";
import {
  AuthRoutes,
  MainRoutes,
  DataroomRoutes,
  AdminRoutes,
} from "./routes/LazyRoutes";
import HomePage from "./pages/HomePage";
import AdaptationOptionsPage from "./pages/Dataroom/AdaptationOptionsPage";
import { TooltipProvider } from "./components/ui/tooltip";
import { OnboardingProvider, OnboardingTour } from "./components/onboarding";
import { FEATURES } from "./config/features";

// Composant pour intégrer le logger d'activité
const ActivityLogger = () => {
  useActivityLogger();
  return null;
};

function App() {
  return (
    <Router>
      <QueryProvider>
        <AuthProvider>
          <ActivityLogger />
          <OnboardingProvider>
            <LoadingErrorBoundary>
              <TooltipProvider>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Auth Routes */}
                    <Route path="/login" element={<AuthRoutes.LoginPage />} />
                    <Route
                      path="/register"
                      element={<AuthRoutes.RegisterPage />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<AuthRoutes.ForgotPassword />}
                    />
                    <Route
                      path="/reset-password-confirm"
                      element={<AuthRoutes.ResetPasswordConfirmPage />}
                    />

                    {/* Auth Routes */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <AuthRoutes.ProfilePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Main Routes */}
                    <Route
                      path="/home"
                      element={
                        <ProtectedRoute>
                          <HomePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboardcard"
                      element={
                        <ProtectedRoute>
                          <MainRoutes.DashboardCard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <HomePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/about-tanzania"
                      element={
                        <ProtectedRoute>
                          <MainRoutes.AboutTanzaniaPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Dataroom Routes */}
                    <Route
                      path="/dashboard/overview"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.Overview />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/stressors"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.ClimateIndicators />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/profile"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.ClimateProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/impacts"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.ClimateImpacts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/economics"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.Economics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/risk-indices"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.RiskIndices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/risk-indices-2"
                      element={
                        FEATURES.cropsAnalysis ? (
                          <ProtectedRoute>
                            <DataroomRoutes.RiskIndices2 />
                          </ProtectedRoute>
                        ) : (
                          <Navigate to="/dashboard/overview" replace />
                        )
                      }
                    />
                    <Route
                      path="/dashboard/adaptation-options"
                      element={
                        <ProtectedRoute>
                          <AdaptationOptionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/risk"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.RiskAssessment />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dataroom/climate-profile/historical-period/:cityName?"
                      element={
                        <ProtectedRoute>
                          <DataroomRoutes.HistoricalPeriodPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin Routes */}
                    <Route
                      path="/admin/user-management"
                      element={
                        <AdminRoute>
                          <AdminRoutes.UserManagement />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/logs"
                      element={
                        <AdminRoute>
                          <AdminRoutes.Log />
                        </AdminRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </TooltipProvider>
            </LoadingErrorBoundary>
            <OnboardingTour />
          </OnboardingProvider>
        </AuthProvider>
        <Toaster />
      </QueryProvider>
    </Router>
  );
}

export default App;
